import { body, coverBg, edge, fade, gloss, mix, n, rng, shade, shadowInk, star, tint, warm, type ArtPal } from './kit';

/**
 * Mahjong Tables (广东麻将): a square felt table seen from above, slightly turned, with the four walls of face-down
 * tiles (red lacquer backs over an ivory face layer) around a small gold centre ring and a few face-down discards.
 * Pure geometry — no faces, glyphs, letters or numbers on the tiles. Placeholder art until the final graphics — see
 * docs/art-prompts.md (no clocks, no green hats, no white/black mourning colours; seven tiles per wall, five
 * discards — no other groups of four besides the four sides the game itself has).
 */
export function mahjongTableCover(P: ArtPal, p: string): string {
  const cx = 440;
  const cy = 200;
  const HALF = 150; // table top half-size (rim included)
  const FELT = 132; // felt half-size
  const PER_WALL = 7;
  const TW = 19; // tile width along the wall
  const TD = 25; // tile depth (lying tile seen from above)
  const PITCH = 20;
  const IN = 74; // inner edge of every wall (distance from the centre)

  const top = P.dark ? mix(P.bg, P.red, 0.1) : tint(P.red, 0.92);
  const mid = P.dark ? mix(P.panel, P.gold, 0.06) : tint(P.gold, 0.9);
  const bg = coverBg(p, P, top, mid, P.bg, [cx, cy]);
  const gold = warm(P);
  const lacquer = P.dark ? shade(P.red, 0.25) : P.red;
  const ivory = P.dark ? '#F3EAD6' : '#FBF4E4';
  const felt = P.dark ? mix(P.panel, P.teal, 0.3) : tint(P.teal, 0.55);
  const wood = P.dark ? mix(P.panel, P.orange, 0.45) : shade(P.orange, 0.15);
  const ink = shadowInk(P);
  const sh = P.dark ? 0.35 : 0.12;

  // one face-down tile in table coordinates: soft shadow, ivory face layer peeking out below, lacquer back on top
  const tile = (x: number, y: number, w: number, h: number) =>
    `<rect x="${n(x + 2)}" y="${n(y + 4)}" width="${w}" height="${h}" rx="3" fill="${ink}" fill-opacity="${sh}"/>` +
    `<rect x="${n(x)}" y="${n(y + 2.5)}" width="${w}" height="${h}" rx="3" fill="${ivory}" stroke="${shade(ivory, 0.3)}" stroke-width="1"/>` +
    `<rect x="${n(x)}" y="${n(y)}" width="${w}" height="${h}" rx="3" fill="url(#${p}bk)" stroke="${edge(lacquer, P)}" stroke-width="1"/>`;

  // the four walls: along each side, seven tiles; horizontal walls lie across, vertical walls along the y axis
  let walls = '';
  const x0 = -((PER_WALL - 1) * PITCH + TW) / 2;
  for (let i = 0; i < PER_WALL; i++) {
    const u = x0 + i * PITCH;
    walls += tile(u, -IN - TD, TW, TD); // north
    walls += tile(u, IN, TW, TD); // south
    walls += tile(-IN - TD, u, TD, TW); // west
    walls += tile(IN, u, TD, TW); // east
  }

  // five face-down discards scattered around the centre ring
  const r = rng(88);
  let discards = '';
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.4 + (r() - 0.5) * 0.3;
    const d = 38 + r() * 8;
    const x = Math.cos(a) * d;
    const y = Math.sin(a) * d;
    discards += `<g transform="rotate(${n((r() - 0.5) * 40)} ${n(x)} ${n(y)})">${tile(x - 8, y - 10.5, 16, 21)}</g>`;
  }

  const centre =
    `<circle r="17" fill="none" stroke="${gold}" stroke-width="2.5" stroke-opacity=".85"/>` +
    `<circle r="10" fill="url(#${p}md)" stroke="${edge(P.gold, P)}" stroke-width="1"/>` +
    `<polygon points="${star(0, 0, 13, 5, 8)}" fill="${gold}" fill-opacity=".35"/>`;

  const table =
    `<rect x="${-HALF + 8}" y="${-HALF + 14}" width="${HALF * 2}" height="${HALF * 2}" rx="26" fill="${ink}" fill-opacity="${P.dark ? 0.45 : 0.14}"/>` +
    `<rect x="${-HALF}" y="${-HALF}" width="${HALF * 2}" height="${HALF * 2}" rx="26" fill="url(#${p}wd)" stroke="${edge(wood, P)}" stroke-width="2"/>` +
    `<rect x="${-FELT}" y="${-FELT}" width="${FELT * 2}" height="${FELT * 2}" rx="14" fill="url(#${p}ft)"/>` +
    `<rect x="${-FELT + 6}" y="${-FELT + 6}" width="${FELT * 2 - 12}" height="${FELT * 2 - 12}" rx="10" fill="none" stroke="${gold}" stroke-opacity=".45" stroke-width="1.5"/>` +
    gloss(-HALF + 30, -HALF + 12, 26, 5, -8, 0.35);

  const glow = `<ellipse cx="${cx}" cy="${cy}" rx="230" ry="190" fill="url(#${p}gw)"/>`;
  const scene = `<g transform="translate(${cx} ${cy}) rotate(-12)">${table}${centre}${discards}${walls}</g>`;

  // gold sparkles around the table
  let sparkles = '';
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI * 0.95 + (i / 6) * Math.PI * 1.1 + (r() - 0.5) * 0.2;
    const d = 200 + r() * 24;
    sparkles += `<polygon points="${star(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.85, 3.5 + r() * 4, 1.3 + r() * 0.8, 4)}" fill="${i % 2 ? gold : tint(gold, 0.35)}"/>`;
  }

  const defs =
    `<defs>${bg.defs}` +
    body(`${p}bk`, lacquer, 0.35, 0.35, 0.6, 1) +
    body(`${p}wd`, wood, 0.3, 0.3, 0.6, 1) +
    body(`${p}ft`, felt, 0.18, 0.25, 0.7, 1) +
    body(`${p}md`, P.gold, 0.45, 0.3, 0.5, 1) +
    fade(`${p}gw`, gold, P.dark ? 0.22 : 0.35) +
    `</defs>`;

  return defs + bg.body + glow + scene + sparkles;
}
